import React, { useState, useMemo } from 'react';
import { Checkbox } from '@shared/ui/checkbox';
import { useFormat } from '../../utils';
import { Button } from '@shared/ui/button';
import { Input } from '@shared/ui/input';
import { Search } from 'lucide-react';
import { Badge } from '@shared/ui/badge';
import { useChoices } from '@features/mx/device-management/hooks';
import { useRemoteUtilityId } from '@shared/selectors/globalSelectors';
import { logEvent } from '@shared/analytics/analytics';

const FieldSelector = () => {
  const { state, dispatch } = useFormat();
  const [searchTerm, setSearchTerm] = useState("");
  const remoteUtilityId = useRemoteUtilityId();
  const { data: fieldTypes } = useChoices({
    choice: "read_format_fields",
    remoteUtilityId,
  });

  const allFields = useMemo(() => {
    if (!fieldTypes) return [];
    return fieldTypes.map((item: any) => ({
      id: item.key,
      fieldName: item.value,
      inputMethod: item.inputMethod || "manual",
    }));
  }, [fieldTypes]);

  const selectedFieldIds = useMemo(
    () => state.selectedFields.map((f) => f.id),
    [state.selectedFields]
  );

  const filteredFields = useMemo(() => {
    if (!searchTerm) return allFields;
    return allFields.filter((field) =>
      field.fieldName.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allFields, searchTerm]);

  const handleToggleField = (field: any) => {
    dispatch({ type: "TOGGLE_FIELD", payload: { field } });
  };

  const handleSelectField = (field: any) => {
    dispatch({ type: "SELECT_FIELD", payload: { field } });
    logEvent('MX Format Field Selected', {
      field,
    })
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold mb-1">Available Fields</h2>
        <p className="text-sm text-gray-600 mb-3">
          Choose fields to include in your format
        </p>
      </div>

      <div className="relative">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-500" />
        <Input
          placeholder="Search fields..."
          className="pl-8"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </div>

      <div className="space-y-2 max-h-[600px] overflow-y-auto pr-2">
        {filteredFields.length === 0 ? (
          <div className="text-center py-4 text-gray-500">
            No fields match your search
          </div>
        ) : (
          filteredFields.map((field) => {
            const isSelected = selectedFieldIds.includes(field.id);

            return (
              <FieldItem
                key={field.id}
                field={field}
                isSelected={isSelected}
                onToggle={() => handleToggleField(field)}
                onConfigure={() => handleSelectField(field)}
              />
            );
          })
        )}
      </div>
    </div>
  );
};

interface FieldItemProps {
  field: any;
  isSelected: boolean;
  onToggle: () => void;
  onConfigure: () => void;
}

const FieldItem = ({
  field,
  isSelected,
  onToggle,
  onConfigure,
}: FieldItemProps) => {
  const getFieldTypeTag = () => {
    if (field.inputMethod === "manual" || field.inputMethod === "photo") {
      return (
        <Badge variant="outline" className="text-xs bg-blue-50 text-blue-700">
          Input
        </Badge>
      );
    } else {
      return (
        <Badge variant="outline" className="text-xs bg-green-50 text-green-700">
          Information
        </Badge>
      );
    }
  };

  return (
    <div
      className={`flex items-center justify-between p-2 border border-gray-200 rounded-md hover:bg-gray-50 ${
        isSelected ? "bg-gray-50 border-gray-300" : "bg-white"
      }`}
    >
      <div className="flex items-center space-x-2 flex-1">
        <Checkbox
          id={field.id}
          checked={isSelected}
          onCheckedChange={onToggle}
        />
        <div className="flex flex-col flex-1">
          <div className="flex items-center gap-2">
            <label
              htmlFor={field.id}
              className="font-medium cursor-pointer text-sm"
            >
              {field.fieldName}
            </label>
          </div>
        </div>
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={onConfigure}
        className="h-7 bg-gray-100"
      >
        Configure
      </Button>
    </div>
  );
};

export default FieldSelector;
